// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import ImagesBlock from 'components/community/discussions/writer/ImagesBlock'
import { PHOTOS_COPY } from 'lib/owner-notes/review-photos'
import type { ReviewPhotosState } from './useReviewPhotos'
import styles from './OwnerNoteBand.module.css'

interface ReviewPhotosBlockProps {
  /** `useReviewPhotos` — the band owns the set; this only draws it. */
  state: Pick<
    ReviewPhotosState,
    'photos' | 'previews' | 'notice' | 'room' | 'add' | 'remove' | 'retry'
  >
  /** A session: uploads need the bearer, so the picker is for signed-in readers only. */
  signedIn: boolean
  /** This publish can carry the photos (a signed profile); false = the tiles say they are left out. */
  canAttach: boolean
  onSignIn: () => Promise<void>
  /** false = the form already offers the sign-in (the band's pre-publish line): keep the
   *  "Sign in to add photos." text, drop the second button. */
  signInButton?: boolean
  /** A gated create (the sign-in gate): sign-in is required at Publish anyway, so a signed-out
   *  reader gets the signed-in picker — no "Sign in to add photos" line, no second sign-in. The
   *  picked files wait, queued, for the gate's session (`useReviewPhotos({holdForSession})`). */
  gated?: boolean
}

/**
 * Step 3's public photos (contract-review-photos): the discussions writer's photo set
 * (`ImagesBlock` — drop zone, grid, `PhotoTile` progress / ✕ / retry), no caption, up to four.
 * Signed out: "Sign in to add photos" and the band's sign-in button, never the picker — a
 * restored set stays drawn with the line that says it will not go without a sign-in.
 */
const ReviewPhotosBlock: React.VFC<ReviewPhotosBlockProps> = ({
  state,
  signedIn,
  canAttach,
  onSignIn,
  signInButton = true,
  gated = false,
}) => {
  const { photos } = state
  const pickable = signedIn || gated
  return (
    <div className={styles.group} role="group" aria-labelledby="owner-photos-legend">
      <p id="owner-photos-legend" className={styles.legend}>
        {PHOTOS_COPY.legend}
      </p>
      {pickable || photos.length > 0 ? (
        <ImagesBlock
          images={photos}
          previews={state.previews}
          coverKey={null}
          room={pickable ? state.room : 0}
          notice={state.notice}
          onFiles={state.add}
          onRemove={state.remove}
          onRetry={state.retry}
          inputLabel="Add photos of yours"
          compact
          queuedLabel={gated && !signedIn ? null : undefined}
        />
      ) : null}
      {!pickable && (
        <p className={styles.attached}>
          {signInButton ? `${PHOTOS_COPY.signIn} ` : PHOTOS_COPY.signIn}
          {signInButton && (
            <button type="button" className={styles.inlineLink} onClick={() => void onSignIn()}>
              {'Sign in'}
            </button>
          )}
        </p>
      )}
      {photos.length > 0 && !canAttach && <p className={styles.attached}>{PHOTOS_COPY.leftOut}</p>}
      {pickable && <p className={styles.fine}>{PHOTOS_COPY.fine}</p>}
    </div>
  )
}

export default ReviewPhotosBlock
